"use client";

/**
 * useFileMetadata — extract duration from audio/video files using DOM elements.
 */

import { useCallback } from "react";

export interface FileMetadata {
  duration: number;
  extension: string;
  mediaCategory: "audio" | "video" | "text";
}

/**
 * Extract duration from an audio or video file using a DOM element.
 * Returns duration in seconds.
 */
function extractDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const isAudio = file.type.startsWith("audio");
    const element = isAudio
      ? document.createElement("audio")
      : document.createElement("video");

    const objectUrl = URL.createObjectURL(file);

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
      element.removeAttribute("src");
      element.load();
    };

    element.addEventListener("loadedmetadata", () => {
      if (
        !isFinite(element.duration) ||
        element.duration === Infinity ||
        isNaN(element.duration)
      ) {
        element.currentTime = 1e101;
        element.ontimeupdate = () => {
          element.ontimeupdate = null;
          const duration = element.duration;
          element.currentTime = 0;
          cleanup();
          resolve(isFinite(duration) ? duration : 0);
        };
      } else {
        const duration = element.duration;
        cleanup();
        resolve(duration);
      }
    });

    element.addEventListener("error", (event) => {
      cleanup();
      reject(
        new Error(
          `Failed to load media metadata: ${(event as ErrorEvent).message || "unknown error"}`,
        ),
      );
    });

    element.src = objectUrl;
  });
}

export function useFileMetadata() {
  /**
   * Extract duration from an audio or video file.
   * Returns 0 if duration cannot be determined (still allows upload for paid plans).
   */
  const getDuration = useCallback(async (file: File): Promise<number> => {
    try {
      const duration = await extractDuration(file);
      return duration;
    } catch {
      // Duration extraction failed
      return 0;
    }
  }, []);

  return { getDuration };
}
